import React, {createRef, ReactNode, RefObject} from "react";
import AbstractViewer, {AbstractViewerProperties, AbstractViewerState} from "../AbstractViewer";
import {ViewerMode} from "../ViewerMode";
import MouseMode from "../settings/MouseMode";
import DomHelper from "../../../shared/helper/DomHelper";
import Exception from "../../../shared/exception/Exception";
import PortalDocument from "../../document/PortalDocument";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import {MessageType} from "../../../shared/log/MessageInterface";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {Archive, FilesObject} from 'libarchive.js/src/libarchive';
import {CompressedFile} from 'libarchive.js/src/compressed-file';
import FileHelper, {PATH_SEPARATOR} from "../../../shared/helper/FileHelper";
import ErrorCode from "../../../shared/exception/ErrorCode";
import {DataTableSelectEvent, DataTableUnselectEvent} from "primereact/datatable";
import {Button} from "primereact/button";
import {Password} from "primereact/password";
import ConfirmZipExtractionDialog from "../../dialog/components/document/ConfirmZipExtractionDialog";
import {Checkbox} from "primereact/checkbox";

const urlCreator = FunctionHelper.applyValue(window.URL, window.webkitURL);

export interface ZipViewerProperties extends AbstractViewerProperties {
}

export interface ZipViewerState extends AbstractViewerState {
	viewerData: Array<File | CompressedFile>;
	loading: boolean;
	exception?: Exception;
	extracting: Array<string>;
	selection: Array<string>;
	zipFiles: FilesObject;
	hasPassword: boolean;
	password: string;
	showExtractConfirmDialog: boolean;
}

export class ZipViewerDefinition extends AbstractViewer<ZipViewerProperties, ZipViewerState> {
	private loaderElement: RefObject<HTMLDivElement>;

	public static defaults(): ZipViewerProperties {
		return {
			viewerMode: ViewerMode.DEFAULT,
			mouseMode: MouseMode.NONE
		} as ZipViewerProperties;
	}

	constructor(props: Readonly<ZipViewerProperties>) {
		super(props);

		this.state = {
			...this.state,
			loading: true,
			extracting: [],
			selection: [],
			zipFiles: {},
			hasPassword: false,
			password: "",
			showExtractConfirmDialog: false
		};

		this.loaderElement = createRef<HTMLDivElement>();
		this.onSelect = this.onSelect.bind(this);
		this.onUnselect = this.onUnselect.bind(this);
		this.onSelectAll = this.onSelectAll.bind(this);
		this.handlePassword = this.handlePassword.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.renderEmptyMessage = this.renderEmptyMessage.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		window.addEventListener('keydown', this.handleKeydown);

		if (this.loaderElement.current !== null) {
			// Add active class after loader is mounted so the fade animation is applied
			DomHelper.addClass(this.loaderElement.current, "active");
		}

		try {
			await super.componentDidMount();
			await this.updateViewerData();
		} catch (exception: any) {
			if (exception.message.includes("Passphrase required")) {
				this.setState({
					loading: false,
					exception: exception,
					hasPassword: true
				});
				return;
			}

			this.setState({
				loading: false,
				exception: exception
			});
			return;
		}

		this.setState({
			loading: false,
			exception: undefined
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		window.removeEventListener('keydown', this.handleKeydown);

		this.setState({
			loading: true,
			exception: undefined
		});

		await super.componentWillUnmount();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<ZipViewerProperties>, prevState: Readonly<ZipViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.attachmentKey !== prevProps.attachmentKey
		];

		if (documentChanged.includes(true)) {
			this.setState({
				loading: true,
				exception: undefined
			});

			try {
				await super.componentWillUnmount();
				await super.componentDidMount();
				await this.updateViewerData();
			} catch (exception: any) {
				this.setState({
					loading: false,
					exception: exception
				});
				return;
			}

			this.setState({
				loading: false,
				exception: undefined
			});
		}
	}

	private async downloadFile(filename: string): Promise<void> {
		let file: File | CompressedFile | undefined;

		for (let data of this.state.viewerData) {
			if (filename === data.name) {
				file = data;
				break;
			}
		}

		if (!FunctionHelper.exists(file)) {
			return;
		}

		let url = urlCreator.createObjectURL(
			file instanceof CompressedFile ? await file.extract() : file
		);

		await FileHelper.download([{
			name: file!.name as string,
			url: url
		}]);
	}

	private async extractFiles(selection: Array<string>): Promise<void> {
		let extractingFiles: Array<string> = this.state.extracting;
		for (let selected of selection) {
			if (!extractingFiles.includes(selected)) {
				extractingFiles.push(selected);
			}
		}

		this.setState({
			selection: [],
			extracting: extractingFiles
		}, async () => {
			for (let [index, selected] of selection.entries()) {
				await this.extractFile(selected, index + 1 === selection.length);
				extractingFiles.splice(extractingFiles.indexOf(selected), 1);
			}

			this.setState({
				selection: [],
				extracting: extractingFiles
			});
		});
	}

	private async extractFile(filename: string, select?: boolean): Promise<void> {
		let file: File | CompressedFile | undefined;

		for (let data of this.state.viewerData) {
			if (filename === data.name) {
				file = data;
				break;
			}
		}

		try {
			let uploadedDocument: PortalDocument = await this.props.context.documentManager.uploadDocument(
				file instanceof CompressedFile ? await file.extract() : file
			);

			if (select) {
				this.props.context.documentManager.select(uploadedDocument.getSourceDocumentId()!, true);
			}
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.extractFile(filename);
			} else {
				await this.props.context.session.handleException(portalException)
			}
		}
	}

	private buildFileList(files: FilesObject, folderName?: string): Array<File | CompressedFile> {
		let fileList: Array<File | CompressedFile> = [];

		for (let [name, file] of Object.entries(files)) {
			if (FunctionHelper.exists(folderName)) {
				name = folderName + PATH_SEPARATOR + name;
			}

			if (file instanceof File || file instanceof CompressedFile) {
				fileList.push(file);
			} else {
				//handle folder
				fileList = fileList.concat(this.buildFileList(file as FilesObject, name));
			}
		}

		return fileList;
	}

	private async updateViewerData(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let zipDocument: Archive;
		if (this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
			zipDocument = await portalDocument.loadViewerData(attachment);
		} else {
			zipDocument = await portalDocument.loadViewerData();
		}

		if (!FunctionHelper.isEmpty(this.state.password)) {
			await zipDocument.usePassword(this.state.password);
		}

		let zipFiles: FilesObject = {};
		try {
			zipFiles = await zipDocument.extractFiles();
		} catch (exception: any) {
			let portalException: Exception = new Exception(exception.message);
			portalException.originalException = exception;
			throw portalException;
		}

		this.setState({
			zipFiles: zipFiles,
			viewerData: this.buildFileList(zipFiles)
		});
	}

	/**
	 * called on select all
	 */
	private onSelectAll(files: FilesObject): Array<string> {
		let selection: Array<string> = [];

		for (let file of Object.values(files)) {
			if (file instanceof CompressedFile || file instanceof File) {
				selection.push(file.name);
			} else {
				selection = selection.concat(this.onSelectAll(file));
			}
		}

		return selection;
	}

	/**
	 * called on unselecting a file
	 */
	private onUnselect(event: DataTableUnselectEvent): void {
		event.originalEvent.stopPropagation();
		let fileEntry: File = event.data;
		let selection: Array<string> = [];

		if (event.type !== "row") {
			for (let entry of this.state.selection) {
				if (entry === fileEntry.name) {
					continue;
				}

				selection.push(entry);
			}
		} else {
			selection = [fileEntry.name];
		}

		this.setState({
			selection: selection
		});
	}

	/**
	 * called on selecting a file
	 */
	private onSelect(event: DataTableSelectEvent): void {
		let fileEntry: File = event.data;
		let selection: Array<string> = [];

		if (event.type !== "row") {
			for (let entry of this.state.selection) {
				if (entry === fileEntry.name) {
					continue;
				}

				selection.push(entry);
			}
		}

		selection.push(fileEntry.name);

		this.setState({
			selection: selection
		});
	}


	/**
	 * Handles keyboard key down events
	 */
	private async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (event.key.toLowerCase() === "enter" && this.state.hasPassword) {
			await this.handlePassword();
		}
	}

	private async handlePassword(): Promise<void> {
		try {
			await this.updateViewerData();

			this.setState({
				exception: undefined,
				hasPassword: false
			});
		} catch (exception: any) {
			this.setState({
				exception: new Exception(Translate.value("viewerscreen.message.wrong_password", {ns: "screen"}))
			});
			return;
		}
	}

	/**
	 * Message to render if the list is empty
	 */
	private renderEmptyMessage(): ReactNode {
		return (
			<p className="no-results">
				<Translate {...this.props} data="viewerscreen.zip.empty" namespace="screen"/>
			</p>
		);
	}

	private renderPasswordPanel(): ReactNode {
		let error: Exception | undefined = this.state.exception;

		return (
			<div className={"viewer message"} ref={this.viewerElement}>
				{this.props.viewerMode === ViewerMode.PREVIEW &&
					<div className="fallback-icon">
						<span className="icon icon-locked password"/>
					</div>
				}
				{this.props.viewerMode !== ViewerMode.PREVIEW &&
					<div className="panel">
						<div className="p-col-12">
							<MessageBox type={MessageType.INFO} closeable={false} language={i18next.language}>
								<Translate {...this.props} data="viewerscreen.message.password" namespace="screen"/>
							</MessageBox>
							{FunctionHelper.exists(error) &&
								<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
											language={i18next.language}
											onClose={() => this.setState({
												exception: undefined
											})}
											stacktrace={
												<>
													<p>{error!.message} ({error!.code})</p>
													<pre>{error!.stack}</pre>
												</>
											}>
									{error!.message}
								</MessageBox>
							}
						</div>
						<div className="p-grid">
							<div className="p-col-12 p-grid p-jc-center">
								<label className="p-mr-2" htmlFor="panel-password">
									<Translate {...this.props} data="viewerscreen.password.label" namespace="screen"/>
								</label>
								<div className="p-mr-2">
									<Password feedback={false} inputId="panel-password" toggleMask={true}
											  onChange={(e) => this.setState({
												  password: e.currentTarget.value
											  })}/>
								</div>
								<Button onClick={this.handlePassword} className="primary-button">
									<Translate {...this.props} data="viewerscreen.password.button" namespace="screen"/>
								</Button>
							</div>
						</div>
					</div>
				}
			</div>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading) {
			return (
				<div className="viewer zip" ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		if (this.state.hasPassword) {
			return this.renderPasswordPanel();
		}

		if (FunctionHelper.exists(this.state.exception)) {
			return (
				<div className="viewer zip" ref={this.viewerElement}>
					{(this.props.viewerMode === ViewerMode.PREVIEW || this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW) &&
						<div className="fallback-icon">
							<span className="icon icon-page"/>
						</div>
					}
					{this.props.viewerMode !== ViewerMode.PREVIEW && this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
						<MessageBox type={MessageType.DANGER} language={i18next.language}>
							{this.state.exception?.message}
						</MessageBox>
					}
				</div>
			);
		}

		if (this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			return (
				<div className="fallback-icon">
					<span className="icon icon-page"/>
				</div>
			);
		}

		let selection: Array<string> = [];
		for (let selected of this.state.selection) {
			selection.push(selected);
		}

		let tableItems: Array<ReactNode> = [];
		let allFiles: Array<string> = [];
		for (let data of this.state.viewerData) {
			let isSelected: boolean = this.state.selection.includes(data.name);
			let lastModified: number = data instanceof File ? data.lastModified : 0;
			allFiles.push(data.name);

			let exportClasses: Array<string> = ["icon"];
			if (this.state.extracting.includes(data.name)) {
				exportClasses.push("icon-spinner");
				exportClasses.push("spin");
			} else {
				exportClasses.push("icon-export");
				exportClasses.push("small");
			}

			tableItems.push(
				<tr key={data.name} className={isSelected ? "selected" : ""} onClick={(event) => {
					this.setState({
						selection: [data.name]
					});
				}}>
					<td onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();

						if (selection.includes(data.name)) {
							selection.splice(selection.indexOf(data.name), 1);
						} else {
							selection.push(data.name);
						}

						this.setState({
							selection: selection
						});
					}}>
						<Checkbox checked={isSelected}/>
					</td>
					<td>
						<span>{data.name}</span>
					</td>
					<td>
						<span>{FileHelper.formatFileSize(FunctionHelper.applyValue(data.size, 0), 2)}</span>
					</td>
					<td>
						<span>{new Date(lastModified).toLocaleString(i18next.language)}</span>
					</td>
					<td>
						<Button icon="icon icon-download" tooltipOptions={{position: 'left'}}
								tooltip={Translate.value(
									"viewerscreen.zip.download", {ns: "screen"}
								)}
								onClick={async (event) => {
									event.preventDefault();
									event.stopPropagation();

									await this.downloadFile(data.name);
								}}/>
						<Button icon={exportClasses.join(" ")} tooltipOptions={{position: 'left'}}
								tooltip={Translate.value(
									"viewerscreen.zip.export", {ns: "screen", count: 1}
								)}
								disabled={this.state.extracting.length > 0}
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();

									let extractingFiles: Array<string> = this.state.extracting;
									if (!extractingFiles.includes(data.name)) {
										extractingFiles.push(data.name);
									}

									this.setState({
										selection: [],
										extracting: extractingFiles
									}, async () => {
										await this.extractFile(data.name, true);

										extractingFiles = this.state.extracting;
										if (extractingFiles.includes(data.name)) {
											extractingFiles.splice(
												extractingFiles.indexOf(data.name), 1
											);
										}

										this.setState({
											selection: [],
											extracting: extractingFiles
										});
									});
								}}/>
					</td>
				</tr>
			);
		}

		let allSelected: boolean = this.state.selection.length === tableItems.length;

		return (
			<>
				<div className="viewer zip" ref={this.viewerElement}>
					<div className="zip-viewer">
						<table>
							<thead>
							<tr>
								<th className="column column-select">
									<Checkbox checked={allSelected} onChange={() => {
										this.setState({
											selection: allSelected ? [] : this.onSelectAll(this.state.zipFiles)
										})
									}}/>
								</th>
								<th className="column column-filename">
									<Translate {...this.props} data="viewerscreen.zip.name" namespace="screen"/>
								</th>
								<th className="column column-filesize">
									<Translate {...this.props} data="viewerscreen.zip.size" namespace="screen"/>
								</th>
								<th className="column column-filedate">
									<Translate {...this.props} data="viewerscreen.zip.date" namespace="screen"/>
								</th>
								<th className="column column-functions">
									<Translate {...this.props} data="viewerscreen.zip.functions" namespace="screen"/>
								</th>
							</tr>
							</thead>
							<tbody>{tableItems}</tbody>
						</table>
						<div className="toolbar">
							<label>
								<span>
									<Translate {...this.props} data="viewerscreen.zip.selected" namespace="screen"/>
								</span>
							</label>
							<Button icon="icon icon-export small" disabled={this.state.selection.length === 0}
									tooltip={Translate.value(
										"viewerscreen.zip.export", {ns: "screen", count: this.state.selection.length}
									)}
									onClick={async () => {
										await this.extractFiles(this.state.selection);
									}}/>
							<label style={{marginLeft: "auto"}}>
								<span>
									<Translate {...this.props} data="viewerscreen.zip.all" namespace="screen"/>
								</span>
							</label>
							<Button icon="icon icon-export small" tooltipOptions={{position: 'left'}}
									tooltip={Translate.value(
										"viewerscreen.zip.export", {ns: "screen", count: allFiles.length}
									)}
									disabled={allFiles.length === 0}
									onClick={async () => {
										if (allFiles.length > 10) {
											this.setState({
												showExtractConfirmDialog: true
											});
										} else {
											await this.extractFiles(allFiles);
										}
									}}/>
						</div>
					</div>
				</div>
				<ConfirmZipExtractionDialog {...this.props} showHelp={false}
											visible={this.state.showExtractConfirmDialog}
											hide={() => this.setState({showExtractConfirmDialog: false})}
											accept={async () => {
												await this.extractFiles(allFiles);
											}}/>
			</>
		);
	}
}

const ZipViewer = React.memo(ZipViewerDefinition,
	(prevProps: Readonly<ZipViewerProperties>, nextProps: Readonly<ZipViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		return true;
	}
);

export default ZipViewer;